import { useEffect, useCallback } from "react";
import { useMedia } from "@/contexts/MediaContext";
import { useModel } from "@/contexts/ModelContext";
import Button from "@scripts/react/components/Button";
import { SidebarHeader } from "@/components/layout/SidebarHeader";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import { predictionToAnnotation } from "@/utils/predictions";
import { PromptLabelList } from "../PromptLabel/PromptLabelList";
import { BuildPromptSteps } from "@/types/flows";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { useCurrentMedia } from "@/store";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

export const PromptReviewPredictionsSidebarContent = () => {
    const currentMedia = useCurrentMedia();
    const {
        setAnnotationsForFileAndFrameId,
        setPredictionsForFileAndFrameSegment,
        promptLabels,
        setTextPromptStr
    } = useModel();
    const { currentFramePredictions, allCurrentFrameAnnotations } = useComputedValues();
    const { markFileAsVisited } = useMedia();
    const { track } = useAnalytics();
    const { clearInferenceQueue } = useGetPredictions();
    const { markFileAsApproved } = useModel();

    const handleApproveBoxPrompts = useCallback(
        (classFilter?: string) => {
            if (
                !(
                    currentMedia?.file.id &&
                    currentMedia?.frame?.id &&
                    currentMedia?.frameSegment !== undefined
                )
            )
                return;

            // Track prediction approval
            track({
                event: AnalyticsFlowEvent.APPROVE_BUTTON_CLICKED,
                data: {
                    appSource: AppSource.RAPID,
                    classFilter: classFilter || "all",
                    predictionCount: currentFramePredictions.length,
                    approvedCount: currentFramePredictions.filter((p) => {
                        if (classFilter) return p.class === classFilter;
                        const promptLabel = promptLabels.get(p.class);
                        return promptLabel && p.confidence >= promptLabel.confidenceThreshold;
                    }).length,
                    isAllClasses: !classFilter
                }
            });

            const classPredictions = classFilter
                ? currentFramePredictions.filter((p) => p.class === classFilter)
                : currentFramePredictions;

            const classPredictionsIds = new Set(classPredictions.map((p) => p.detection_id));

            const annotationsLabelsToDelete = new Set(classPredictions.map((p) => p.class));

            const approvedPredictions = classPredictions.filter((p) => {
                const promptLabel = promptLabels.get(p.class);
                if (!promptLabel) return false;
                return p.confidence >= promptLabel.confidenceThreshold;
            });

            setAnnotationsForFileAndFrameId((prev) => {
                const current = { ...prev }; // clone to trigger state re-render
                const fileAnnotations = current[currentMedia?.file.id] || {};

                fileAnnotations[currentMedia?.frame?.id!] = [
                    ...approvedPredictions.map((prediction) =>
                        predictionToAnnotation(prediction, currentMedia?.frame?.id!)
                    ),
                    ...(fileAnnotations[currentMedia?.frame?.id!] || []).filter(
                        (a) => !annotationsLabelsToDelete.has(a.label)
                    )
                ];

                current[currentMedia?.file.id] = fileAnnotations;

                return current;
            });

            // Stop any in-flight predictions for this file/frame to avoid repopulating predictions
            try {
                clearInferenceQueue();
            } catch (e) {
                // no-op
            }

            // Clear predictions: if approving all classes, clear all; otherwise clear only approved ones
            setPredictionsForFileAndFrameSegment((prev) => {
                const current = { ...prev }; // clone to trigger state re-render
                const filePredictions = current[currentMedia?.file.id] || {};
                filePredictions[currentMedia?.frameSegment!] = !classFilter
                    ? []
                    : currentFramePredictions.filter(
                          (p) => !classPredictionsIds.has(p.detection_id)
                      );

                current[currentMedia?.file.id] = filePredictions;

                return current;
            });

            // Only clear text prompt if approving all classes
            if (!classFilter) {
                setTextPromptStr("");

                // Mark file as approved and visited when approving all classes
                if (currentMedia?.file.id) {
                    markFileAsApproved(currentMedia?.file.id);
                    markFileAsVisited(currentMedia?.file.id);
                }
            }
        },
        [
            currentMedia?.file.id,
            currentMedia?.frame?.id,
            currentMedia?.frameSegment,
            currentFramePredictions,
            setAnnotationsForFileAndFrameId,
            setPredictionsForFileAndFrameSegment,
            promptLabels,
            setTextPromptStr,
            track,
            clearInferenceQueue,
            markFileAsApproved,
            markFileAsVisited
        ]
    );

    const handleRejectBoxPrompts = useCallback(
        (classFilter?: string) => {
            if (
                !(
                    currentMedia?.file.id &&
                    currentMedia?.frame?.id &&
                    currentMedia?.frameSegment !== undefined
                )
            )
                return;

            // Track prediction rejection
            track({
                event: AnalyticsFlowEvent.REJECT_BUTTON_CLICKED,
                data: {
                    appSource: AppSource.RAPID,
                    classFilter: classFilter || "all",
                    predictionCount: currentFramePredictions.length,
                    rejectedCount: classFilter
                        ? currentFramePredictions.filter((p) => p.class === classFilter).length
                        : currentFramePredictions.length,
                    isAllClasses: !classFilter
                }
            });

            const draftAnnotationsToReenable = new Set(
                (classFilter
                    ? allCurrentFrameAnnotations.filter((a) => a.label === classFilter)
                    : allCurrentFrameAnnotations
                )
                    .filter((a) => a.draft)
                    .map((a) => a.id)
            );

            setAnnotationsForFileAndFrameId((prev) => {
                const current = { ...prev }; // clone to trigger state re-render

                const fileAnnotations = current[currentMedia?.file.id] || {};
                fileAnnotations[currentMedia?.frame?.id!] =
                    fileAnnotations[currentMedia?.frame?.id!]?.map((a) =>
                        draftAnnotationsToReenable.has(a.id) ? { ...a, draft: false } : a
                    ) || [];
                current[currentMedia?.file.id] = fileAnnotations;

                return current;
            });

            setPredictionsForFileAndFrameSegment((prev) => {
                const current = { ...prev }; // clone to trigger state re-render

                const filePredictions = current[currentMedia?.file.id] || {};

                const predictionsToReject = new Set(
                    (classFilter
                        ? filePredictions[currentMedia?.frameSegment!].filter(
                              (p) => p.class === classFilter
                          )
                        : filePredictions[currentMedia?.frameSegment!]
                    ).map((p) => p.detection_id)
                );

                filePredictions[currentMedia?.frameSegment!] =
                    filePredictions[currentMedia?.frameSegment!]?.filter(
                        (p) => !predictionsToReject.has(p.detection_id)
                    ) || [];
                current[currentMedia?.file.id] = filePredictions;

                return current;
            });
        },
        [
            currentMedia?.file.id,
            currentMedia?.frame?.id,
            currentMedia?.frameSegment,
            setAnnotationsForFileAndFrameId,
            setPredictionsForFileAndFrameSegment,
            allCurrentFrameAnnotations,
            track,
            currentFramePredictions
        ]
    );

    const onApproveAll = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleApproveBoxPrompts();
        },
        [handleApproveBoxPrompts]
    );

    const onRejectAll = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleRejectBoxPrompts();
        },
        [handleRejectBoxPrompts]
    );

    // Keyboard shortcuts for approve/reject
    useKeyboardShortcut({ key: "a", handler: handleApproveBoxPrompts });
    useKeyboardShortcut({ key: "r", handler: handleRejectBoxPrompts });

    const promptCardCount = promptLabels.size;
    const isMultiple = promptCardCount > 1;
    const rejectText = isMultiple ? "Reject All" : "Reject";
    const approveText = isMultiple ? "Approve All" : "Approve";

    return (
        <>
            <SidebarHeader
                title="How do the objects look?"
                description={
                    <p>
                        Adjust the {isMultiple ? "sliders" : "slider"} until they look good. You
                        will be able to edit the boxes later.
                    </p>
                }
            />
            <div className="content">
                <div className="grid grid-cols-2 gap-2">
                    <Button
                        type="reject-ghost"
                        aria-label={rejectText}
                        title={rejectText}
                        onClick={onRejectAll}
                    >
                        <i className="far fa-xmark"></i>
                        {rejectText}
                        <div className="hotkey border-transparent bg-white">R</div>
                    </Button>
                    <Button
                        type="success-ghost"
                        aria-label={approveText}
                        title={approveText}
                        onClick={onApproveAll}
                    >
                        <i className="far fa-check"></i>
                        {approveText}
                        <div className="hotkey border-transparent bg-white">A</div>
                    </Button>
                </div>
                <PromptLabelList
                    mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                    onApprove={handleApproveBoxPrompts}
                    onReject={handleRejectBoxPrompts}
                />
            </div>
        </>
    );
};
